import React, { useState, useEffect, useCallback } from 'react';
import { userService } from '../services/user.service';
import { Button, Input } from '../../components/ui/index';
import type { User, UserFilters } from '../types';
import { DEFAULT_PAGE_SIZE } from '../constants';
import { TableSkeleton } from '../components/Skeleton';
import { Pagination } from '../components/Pagination';
import '../../globals.css';

export default function Users() {
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [actionLoadingUserId, setActionLoadingUserId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState<UserFilters>({
    name: '',
    region: '',
    city: '',
    blocked: '',
    page: 1,
    limit: DEFAULT_PAGE_SIZE,
  });

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await userService.getAllUsers({ page: 1, limit: 1000 });
      setAllUsers(response.users || []);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao carregar usuários';
      setError(message);
      setAllUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    let filtered = [...allUsers];

    if (filters.name) {
      const searchName = filters.name.toLowerCase();
      filtered = filtered.filter(user =>
        user.name?.toLowerCase().includes(searchName) ||
        user.email?.toLowerCase().includes(searchName)
      );
    }

    if (filters.region) {
      const searchRegion = filters.region.toLowerCase();
      filtered = filtered.filter(user =>
        user.locations?.some(loc => loc.region.toLowerCase().includes(searchRegion))
      );
    }

    if (filters.city) {
      const searchCity = filters.city.toLowerCase();
      filtered = filtered.filter(user =>
        user.locations?.some(loc => loc.city.toLowerCase().includes(searchCity))
      );
    }

    if (filters.blocked === true) {
      filtered = filtered.filter((user) => user.isBlocked);
    }

    if (filters.blocked === false) {
      filtered = filtered.filter((user) => !user.isBlocked);
    }

    setTotal(filtered.length);
    const pages = Math.ceil(filtered.length / (filters.limit || DEFAULT_PAGE_SIZE));
    setTotalPages(pages || 1);

    const startIndex = ((filters.page || 1) - 1) * (filters.limit || DEFAULT_PAGE_SIZE);
    const endIndex = startIndex + (filters.limit || DEFAULT_PAGE_SIZE);
    setUsers(filtered.slice(startIndex, endIndex));
  }, [allUsers, filters]);

  const handleSearch = () => {
    setFilters({ ...filters, page: 1 });
  };

  const handleClearFilters = () => {
    setFilters({
      name: '',
      region: '',
      city: '',
      blocked: '',
      page: 1,
      limit: DEFAULT_PAGE_SIZE,
    });
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleToggleUserBlock = async (user: User) => {
    const nextBlockedState = !user.isBlocked;
    const confirmMessage = nextBlockedState
      ? `Deseja bloquear o usuário "${user.name}"?`
      : `Deseja desbloquear o usuário "${user.name}"?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    let blockedReason: string | undefined;
    if (nextBlockedState) {
      const input = window.prompt('Motivo do bloqueio (opcional):', user.blockedReason || '');
      if (input === null) {
        return;
      }
      blockedReason = input.trim() || undefined;
    }

    try {
      setActionLoadingUserId(user.id);
      setError(null);
      await userService.updateUserBlockStatus(user.id, nextBlockedState, blockedReason);
      await loadUsers();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao atualizar status do usuário';
      setError(message);
    } finally {
      setActionLoadingUserId(null);
    }
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{
          fontSize: '32px',
          fontWeight: 800,
          color: 'var(--foreground)',
          marginBottom: '8px',
          letterSpacing: '-0.5px',
        }}>
          Usuários Cadastrados
        </h1>
        <p style={{ color: 'var(--muted-foreground)', fontSize: '15px', fontWeight: 500 }}>
          <span style={{
            background: 'var(--primary)',
            color: 'white',
            padding: '4px 12px',
            borderRadius: '20px',
            fontSize: '13px',
            fontWeight: 600,
            marginRight: '8px',
          }}>
            {total}
          </span>
          usuário{total !== 1 ? 's' : ''} encontrado{total !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Filtros */}
      <div style={{
        background: 'var(--card)',
        border: '1px solid var(--border)',
        borderRadius: '12px',
        padding: '28px',
        marginBottom: '28px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '24px' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.35-4.35"/>
          </svg>
          <h2 style={{
            fontSize: '18px',
            fontWeight: 700,
            color: 'var(--foreground)',
            margin: 0,
          }}>
            Filtros de Pesquisa
          </h2>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '20px',
          marginBottom: '20px',
        }}>
          <Input
            label="Nome"
            value={filters.name || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFilters({ ...filters, name: e.target.value })
            }
            placeholder="Buscar por nome..."
          />

          <Input
            label="Estado"
            value={filters.region || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFilters({ ...filters, region: e.target.value })
            }
            placeholder="Ex: São Paulo"
          />

          <Input
            label="Cidade"
            value={filters.city || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFilters({ ...filters, city: e.target.value })
            }
            placeholder="Ex: Caçador"
          />

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: '14px', fontWeight: 600, color: 'var(--foreground)' }}>
              Situação
            </label>
            <select
              value={filters.blocked === '' ? '' : String(filters.blocked)}
              onChange={(e) => {
                const value = e.target.value;
                setFilters({
                  ...filters,
                  blocked: value === '' ? '' : value === 'true',
                });
              }}
              style={{
                padding: '12px 14px',
                border: '2px solid var(--border)',
                borderRadius: '8px',
                background: 'var(--input)',
                color: 'var(--foreground)',
                fontSize: '15px',
                outline: 'none',
              }}
            >
              <option value="">Todos</option>
              <option value="false">Ativos</option>
              <option value="true">Bloqueados</option>
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <Button onClick={handleSearch} isLoading={loading}>
            Pesquisar
          </Button>
          <Button variant="secondary" onClick={handleClearFilters}>
            Limpar Filtros
          </Button>
        </div>
      </div>

      {/* Mensagem de erro */}
      {error && (
        <div style={{
          padding: '16px',
          background: 'var(--destructive)',
          color: 'white',
          borderRadius: '8px',
          marginBottom: '24px',
        }}>
          {error}
        </div>
      )}

      {/* Tabela */}
      {loading ? (
        <div style={{
          background: 'var(--card)',
          border: '1px solid var(--border)',
          borderRadius: '12px',
          padding: '24px',
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
        }}>
          <TableSkeleton rows={10} columns={5} />
        </div>
      ) : (
        <>
          <div style={{
            background: 'var(--card)',
            border: '1px solid var(--border)',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
          }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '14px',
              }}>
                <thead>
                  <tr style={{
                    background: 'linear-gradient(to bottom, var(--muted) 0%, var(--card) 100%)',
                    borderBottom: '2px solid var(--border)',
                  }}>
                    <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 700, color: 'var(--foreground)', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>ID</th>
                    <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 700, color: 'var(--foreground)', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Nome</th>
                    <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 700, color: 'var(--foreground)', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Email</th>
                    <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 700, color: 'var(--foreground)', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Telefone</th>
                    <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 700, color: 'var(--foreground)', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Situação</th>
                    <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 700, color: 'var(--foreground)', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Último Login</th>
                    <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 700, color: 'var(--foreground)', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Cadastro</th>
                    <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 700, color: 'var(--foreground)', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr>
                      <td
                        colSpan={8}
                        style={{
                          padding: '64px',
                          textAlign: 'center',
                          color: 'var(--muted-foreground)',
                          fontSize: '15px',
                        }}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.3">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                            <circle cx="12" cy="7" r="4"/>
                          </svg>
                          <span>Nenhum usuário encontrado</span>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    users.map((user, index) => (
                      <tr
                        key={user.id}
                        style={{
                          background: index % 2 === 0 ? 'transparent' : 'var(--muted)',
                          borderBottom: '1px solid var(--border)',
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'var(--accent)';
                          e.currentTarget.style.transform = 'scale(1.01)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                          const cells = e.currentTarget.querySelectorAll('td');
                          cells.forEach((cell: any) => {
                            cell.style.color = 'white';
                          });
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = index % 2 === 0 ? 'transparent' : 'var(--muted)';
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.boxShadow = 'none';
                          const cells = e.currentTarget.querySelectorAll('td');
                          cells.forEach((cell: any) => {
                            cell.style.color = '';
                          });
                        }}
                      >
                        <td style={{ padding: '16px 20px' }}>
                          <span style={{
                            background: 'var(--primary)',
                            color: 'white',
                            padding: '4px 10px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: 600,
                          }}>
                            #{user.id}
                          </span>
                        </td>
                        <td style={{ padding: '16px 20px', fontWeight: 600, fontSize: '15px' }}>{user.name}</td>
                        <td style={{ padding: '16px 20px', color: 'var(--muted-foreground)' }}>{user.email}</td>
                        <td style={{ padding: '16px 20px' }}>{user.phone || '-'}</td>
                        <td style={{ padding: '16px 20px' }}>
                          <span
                            style={{
                              background: user.isBlocked ? '#b91c1c' : '#15803d',
                              color: 'white',
                              padding: '4px 10px',
                              borderRadius: '999px',
                              fontSize: '12px',
                              fontWeight: 600,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                            }}
                          >
                            {user.isBlocked ? 'Bloqueado' : 'Ativo'}
                          </span>
                          {user.isBlocked && user.blockedReason ? (
                            <div style={{ marginTop: 8, fontSize: '12px', color: 'var(--muted-foreground)' }}>
                              {user.blockedReason}
                            </div>
                          ) : null}
                        </td>
                        <td style={{ padding: '16px 20px', fontSize: '13px' }}>{formatDate(user.lastLogin)}</td>
                        <td style={{ padding: '16px 20px', fontSize: '13px' }}>{formatDate(user.createdAt)}</td>
                        <td style={{ padding: '16px 20px' }}>
                          <Button
                            size="sm"
                            variant={user.isBlocked ? 'secondary' : 'destructive'}
                            isLoading={actionLoadingUserId === user.id}
                            onClick={() => handleToggleUserBlock(user)}
                          >
                            {user.isBlocked ? 'Desbloquear' : 'Bloquear'}
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Paginação */}
          {totalPages > 1 && (
            <Pagination
              currentPage={filters.page || 1}
              totalPages={totalPages}
              onPageChange={(page) => setFilters({ ...filters, page })}
              totalItems={total}
              itemsPerPage={filters.limit || DEFAULT_PAGE_SIZE}
            />
          )}
        </>
      )}
    </div>
  );
}
